import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Ban, Check, Edit, Eye, Loader2, Trash2, Unlink } from 'lucide-react';
import { BackButton } from '@/components/ui/back-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CredentialCardView } from '@/components/secrets/CredentialCardView';
import type { SecretActionButtonsCredential } from '@/components/secrets/SecretActionButtons';
import { SecretModal } from '@/components/secrets/SecretModal';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
    buildCredentialUsageSelectionKey,
    toCredentialUsageReference,
} from '@/lib/secrets/credentialUsageSelection';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    useCredentialUsages,
    useDeleteSecret,
    useDisableSecret,
    useDisconnectCredentialUsages,
    useEnableSecret,
} from '@/lib/hooks/useSecrets';
import { runInBackground } from '@/lib/utils';
import { z } from 'zod';
import { organizationApi } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import type {
    CredentialType,
    CredentialUsageListItem,
    CredentialUsageReference,
    GetMerchantChecklistCheckResponse,
    MerchantChecklistCredentialIssue,
    MerchantChecklistCheckCode,
    MerchantChecklistCheckResult,
    MerchantChecklistIssue,
    MerchantChecklistPermissionIssue,
    SecretUuidReference,
    SecretOrganizationUuidReference,
} from '@pillar-portal/api-types';
import { ThirdPartySystemId } from '@pillar-portal/api-types';

const ThirdPartySystemIdSchema = z.union([
    z.literal(ThirdPartySystemId.CORE),
    z.literal(ThirdPartySystemId.DEALERTRACK),
    z.literal(ThirdPartySystemId.PCPARTPICKER),
    z.literal(ThirdPartySystemId.VALOR_US),
]);

export const MerchantChecklistOrganizationUuidSchema: z.ZodString = z.string().uuid();

function ChecklistSummaryBadge({
    status,
    errorCount,
    warningCount,
}: {
    status: 'pass' | 'error' | 'warning';
    errorCount: number;
    warningCount: number;
}) {
    if (status === 'error') {
        return <Badge variant="destructive">{`${errorCount} errors`}</Badge>;
    }
    if (status === 'warning') {
        return (
            <Badge className="border-transparent bg-yellow-100 text-yellow-800">{`${warningCount} warnings`}</Badge>
        );
    }
    return <Badge className="border-transparent bg-green-100 text-green-800">All checks pass</Badge>;
}

function ChecklistCheckStatusBadge({ check }: { check: MerchantChecklistCheckResult }) {
    if (check.status === 'error') {
        return <Badge variant="destructive">Error</Badge>;
    }
    if (check.status === 'warning') {
        return <Badge className="border-transparent bg-yellow-100 text-yellow-800">Warning</Badge>;
    }
    return <Badge className="border-transparent bg-green-100 text-green-800">Pass</Badge>;
}

const CHECKLIST_CHECK_CODES: MerchantChecklistCheckCode[] = [
    'permission_records_valid',
    'users_physical_terminal_access',
    'users_virtual_terminal_access',
    'merchant_business_details_complete',
    'logical_terminals_configured',
    'terminal_devices_configured',
    'terminal_device_credential_link_integrity',
    'credentials_gateway_connectivity',
    'credentials_inconsistent_by_account_id',
    'credentials_duplicated_by_account_id',
];

const CHECKLIST_LOADING_TITLES: Record<MerchantChecklistCheckCode, string> = {
    permission_records_valid: 'No permission record validation failures',
    users_physical_terminal_access: 'Every active user has access to at least one physical terminal',
    users_virtual_terminal_access: 'Every active user has access to at least one virtual terminal',
    merchant_business_details_complete: 'Merchant Business Details are complete for receipt generation',
    logical_terminals_configured: 'At least one active logical terminal is configured',
    terminal_devices_configured:
        'Active terminal devices include at least one physical and one virtual device',
    terminal_device_credential_link_integrity:
        'Every configured terminal device passes credential link QA checks',
    credentials_gateway_connectivity: 'All active credentials authenticate with the gateway',
    credentials_inconsistent_by_account_id: 'No inconsistent credentials exist for the same account_id',
    credentials_duplicated_by_account_id: 'No duplicated credentials exist for the same account_id',
};

type TerminalIssueRecord = Extract<MerchantChecklistIssue, { issue_type: 'terminal' }>;

type AggregatedTerminalQaIssue = {
    terminal: TerminalIssueRecord['terminal'];
    failedChecks: string[];
};

function formatQaCheck(label: string, pass: boolean) {
    return (
        <div className="text-xs text-muted-foreground">
            {label} <span className={pass ? 'text-emerald-600' : 'text-destructive'}>{pass ? 'PASS' : 'FAIL'}</span>
        </div>
    );
}

export function toChecklistCredentialActionTarget(
    issue: MerchantChecklistCredentialIssue
): SecretActionButtonsCredential | null {
    const parsedSystemId = ThirdPartySystemIdSchema.safeParse(issue.credential.system_id);
    if (!parsedSystemId.success) {
        return null;
    }
    return {
        credential_uuid: issue.credential.credential_uuid,
        account_id: issue.credential.account_id,
        system_id: parsedSystemId.data,
        credential_type: issue.credential.credential_type,
        is_active: issue.credential.is_active,
        applies_to_children: issue.credential.applies_to_children,
    };
}

export function toChecklistSecretIdentityInput(
    credential: Pick<SecretActionButtonsCredential, 'credential_uuid'>
): SecretUuidReference {
    if (credential.credential_uuid === undefined) {
        throw new Error('Missing credential_uuid');
    }
    return {
        credential_uuid: credential.credential_uuid,
    };
}

function getExpectedCredentialType(isVirtual: boolean | null): CredentialType | null {
    if (isVirtual === null) {
        return null;
    }
    return isVirtual ? 'VIRTUAL_TERMINAL' : 'PHYSICAL_TERMINAL';
}

export function getTerminalIssueGroupKey(issue: TerminalIssueRecord): string {
    if (issue.terminal.terminal_device_uuid !== null) {
        return `td:${issue.terminal.terminal_device_uuid}`;
    }
    return `lt:${issue.terminal.logical_terminal_uuid}:${issue.terminal.processor_device_id ?? 'unknown'}`;
}

export function aggregateTerminalQaIssues(issues: MerchantChecklistIssue[]): AggregatedTerminalQaIssue[] {
    const terminalIssues: TerminalIssueRecord[] = issues.filter(
        (issue): issue is TerminalIssueRecord => issue.issue_type === 'terminal'
    );
    const aggregationByTerminalKey = new Map<string, AggregatedTerminalQaIssue>();
    terminalIssues.forEach((terminalIssue) => {
        const terminalKey: string = getTerminalIssueGroupKey(terminalIssue);
        const existingAggregation = aggregationByTerminalKey.get(terminalKey);
        if (existingAggregation === undefined) {
            aggregationByTerminalKey.set(terminalKey, {
                terminal: terminalIssue.terminal,
                failedChecks: [terminalIssue.message],
            });
            return;
        }
        existingAggregation.failedChecks.push(terminalIssue.message);
    });
    return Array.from(aggregationByTerminalKey.values());
}

type TerminalDeviceQaCardProps = {
    terminal: TerminalIssueRecord['terminal'];
    failedChecks: string[];
};

function TerminalDeviceQaCard({ terminal, failedChecks }: TerminalDeviceQaCardProps) {
    const hasCredentialLink: boolean = terminal.credential_uuid !== null;
    const hasTerminalDeviceCredentialType: boolean = terminal.terminal_device_credential_type !== null;
    const terminalDeviceCredentialTypeMatchesLinkedCredentialType: boolean =
        terminal.linked_credential_type !== null &&
        terminal.terminal_device_credential_type !== null &&
        terminal.linked_credential_type === terminal.terminal_device_credential_type;
    const expectedCredentialType: CredentialType | null = getExpectedCredentialType(terminal.is_virtual);
    const terminalVirtualityMatchesLinkedCredentialType: boolean =
        expectedCredentialType !== null &&
        terminal.linked_credential_type !== null &&
        expectedCredentialType === terminal.linked_credential_type;
    return (
        <div className="rounded border p-3 space-y-2">
            <div className="font-medium">
                Logical terminal {terminal.processor_tid}
                {terminal.terminal_device_uuid !== null
                    ? ` / device ${terminal.processor_device_id ?? '(unknown)'}`
                    : ''}
            </div>
            <div className="text-xs text-destructive">{`${failedChecks.length} QA check(s) failed`}</div>
            <div className="space-y-1">
                {formatQaCheck('QA: terminal_device credential link exists:', hasCredentialLink)}
                {formatQaCheck(
                    'QA: terminal_device.credential_type is set:',
                    hasTerminalDeviceCredentialType
                )}
                {formatQaCheck(
                    'QA: terminal_device.credential_type matches linked credential:',
                    terminalDeviceCredentialTypeMatchesLinkedCredentialType
                )}
                {formatQaCheck(
                    'QA: is_virtual matches linked credential type:',
                    terminalVirtualityMatchesLinkedCredentialType
                )}
            </div>
            <div className="text-xs text-muted-foreground">
                terminal_device_uuid={terminal.terminal_device_uuid ?? 'null'}, credential_uuid=
                {terminal.credential_uuid ?? 'null'}, terminal_device.credential_type=
                {terminal.terminal_device_credential_type ?? 'null'}, linked credential_type=
                {terminal.linked_credential_type ?? 'null'}, is_virtual=
                {terminal.is_virtual === null ? 'null' : String(terminal.is_virtual)}
            </div>
        </div>
    );
}

type ChecklistIssueCardProps = {
    issue: MerchantChecklistIssue;
    organizationUuid: string;
    onViewUser: (userUuid: string) => void;
    onReviewPermissionIssue: (issue: MerchantChecklistPermissionIssue) => void;
    onViewCredential: (credential: SecretActionButtonsCredential) => void;
    onEditCredential: (credential: SecretActionButtonsCredential) => void;
    onDisableCredential: (credential: SecretActionButtonsCredential) => void;
    onEnableCredential: (credential: SecretActionButtonsCredential) => void;
    onDeleteCredential: (credential: SecretActionButtonsCredential) => void;
    onDisconnectCredential: (credential: SecretActionButtonsCredential) => void;
};

export function buildChecklistUserHref(userUuid: string): string {
    return `/users/${userUuid}`;
}

export function buildMerchantChecklistPermissionAuditHref(
    organizationUuid: string,
    roleAssignmentUuid: string
): string {
    return `/organizations/${organizationUuid}/permissions-audit?role_assignment_uuid=${roleAssignmentUuid}`;
}

export function buildMerchantChecklistOrganizationAuditHref(
    organizationUuid: string
): string {
    return `/organizations/${organizationUuid}/permissions-audit`;
}

function formatPermissionFindingCodeLabel(code: string): string {
    return code
        .toLowerCase()
        .split('_')
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
}

type ChecklistHeaderActionProps = {
    check: MerchantChecklistCheckResult;
    onOpenMerchantBusinessDetails: () => void;
    onOpenPermissionsAudit: () => void;
};

export function ChecklistTopLevelAction({
    check,
    onOpenMerchantBusinessDetails,
    onOpenPermissionsAudit,
}: ChecklistHeaderActionProps) {
    if (check.status === 'pass') {
        return null;
    }
    if (check.code === 'merchant_business_details_complete') {
        return (
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOpenMerchantBusinessDetails}
            >
                View
            </Button>
        );
    }
    if (check.code === 'permission_records_valid') {
        return (
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOpenPermissionsAudit}
            >
                View
            </Button>
        );
    }
    return null;
}

type ChecklistQueryErrorCardProps = {
    checkCode: MerchantChecklistCheckCode;
    errorMessage: string;
    onOpenMerchantBusinessDetails: () => void;
    onOpenPermissionsAudit: () => void;
};

export function ChecklistQueryErrorCard({
    checkCode,
    errorMessage,
    onOpenMerchantBusinessDetails,
    onOpenPermissionsAudit,
}: ChecklistQueryErrorCardProps) {
    const actionCheck: MerchantChecklistCheckResult = {
        code: checkCode,
        title: CHECKLIST_LOADING_TITLES[checkCode],
        severity: 'error',
        status: 'error',
        details: [],
        issues: [],
    };
    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">
                    {CHECKLIST_LOADING_TITLES[checkCode]}
                </CardTitle>
                <Badge variant="destructive">Error</Badge>
            </CardHeader>
            <CardContent>
                <div className="flex items-start justify-between gap-3">
                    <div className="text-sm text-destructive">{errorMessage}</div>
                    <ChecklistTopLevelAction
                        check={actionCheck}
                        onOpenMerchantBusinessDetails={onOpenMerchantBusinessDetails}
                        onOpenPermissionsAudit={onOpenPermissionsAudit}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

export function parseMerchantChecklistOrganizationUuid(
    rawOrganizationUuid: string | undefined
): string {
    const parseResult = MerchantChecklistOrganizationUuidSchema.safeParse(rawOrganizationUuid);
    if (!parseResult.success) {
        throw new Error(`Invalid organization UUID: ${rawOrganizationUuid}`);
    }
    return parseResult.data;
}

export function buildMerchantChecklistBackHref(organizationUuid: string): string {
    return `/organizations/${organizationUuid}?view=merchant`;
}

export function buildMerchantBusinessDetailsHref(organizationUuid: string): string {
    return `/organizations/${organizationUuid}?view=merchant&edit=business-details`;
}

function ChecklistCredentialActionButtons({
    credential,
    onView,
    onEdit,
    onDisable,
    onEnable,
    onDelete,
    onDisconnect,
}: {
    credential: SecretActionButtonsCredential;
    onView: () => void;
    onEdit: () => void;
    onDisable: () => void;
    onEnable: () => void;
    onDelete: () => void;
    onDisconnect: () => void;
}) {
    return (
        <div className="flex gap-2 ml-4">
            <Button variant="outline" size="sm" onClick={onView} title="View secret details">
                <Eye className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit} title="Edit secret">
                <Edit className="h-4 w-4" />
            </Button>
            {credential.is_active ? (
                <Button variant="outline" size="sm" onClick={onDisable} title="Disable secret">
                    <Ban className="h-4 w-4" />
                </Button>
            ) : (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onEnable}
                    title="Enable secret"
                    className="text-green-700 hover:text-green-800"
                >
                    <Check className="h-4 w-4" />
                </Button>
            )}
            <Button
                variant="outline"
                size="sm"
                onClick={onDelete}
                title="Delete secret"
                className="text-red-600 hover:text-red-700"
            >
                <Trash2 className="h-4 w-4" />
            </Button>
            <Button
                variant="outline"
                size="sm"
                onClick={onDisconnect}
                title="Disconnect this credential from selected usages"
            >
                <Unlink className="h-4 w-4" />
            </Button>
        </div>
    );
}

export function ChecklistIssueCard({
    issue,
    organizationUuid,
    onViewUser,
    onReviewPermissionIssue,
    onViewCredential,
    onEditCredential,
    onDisableCredential,
    onEnableCredential,
    onDeleteCredential,
    onDisconnectCredential,
}: ChecklistIssueCardProps) {
    if (issue.issue_type === 'credential') {
        const credentialActionTarget = toChecklistCredentialActionTarget(issue);
        if (credentialActionTarget === null) {
            return (
                <div className="border rounded-lg p-4 bg-muted/20">
                    <div className="font-medium">{issue.credential.account_id}</div>
                    <div className="text-sm text-muted-foreground">{issue.message}</div>
                </div>
            );
        }
        return (
            <CredentialCardView
                credential={{
                    ...issue.credential,
                    system_id: credentialActionTarget.system_id,
                }}
                showPublicFields={true}
                actions={
                    <ChecklistCredentialActionButtons
                        credential={credentialActionTarget}
                        onView={() => onViewCredential(credentialActionTarget)}
                        onEdit={() => onEditCredential(credentialActionTarget)}
                        onDisable={() => onDisableCredential(credentialActionTarget)}
                        onEnable={() => onEnableCredential(credentialActionTarget)}
                        onDelete={() => onDeleteCredential(credentialActionTarget)}
                        onDisconnect={() => onDisconnectCredential(credentialActionTarget)}
                    />
                }
            />
        );
    }

    if (issue.issue_type === 'permission') {
        return (
            <div className="border rounded-lg p-4 bg-muted/20">
                <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                        <div className="font-medium">{issue.user.display_name}</div>
                        <div className="text-sm text-muted-foreground">{issue.message}</div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatPermissionFindingCodeLabel(issue.finding_code)}</span>
                            {issue.permissions_bundle_name !== null ? (
                                <span>{issue.permissions_bundle_name}</span>
                            ) : null}
                            {issue.permission_index !== null ? (
                                <span>{`Permission #${issue.permission_index + 1}`}</span>
                            ) : null}
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onReviewPermissionIssue(issue)}
                        title={buildMerchantChecklistPermissionAuditHref(
                            organizationUuid,
                            issue.role_assignment_uuid
                        )}
                    >
                        View
                    </Button>
                </div>
            </div>
        );
    }

    if (issue.issue_type === 'terminal') {
        return <TerminalDeviceQaCard terminal={issue.terminal} failedChecks={[issue.message]} />;
    }

    return (
        <div className="border rounded-lg p-4 bg-muted/20">
            <div className="flex items-center justify-between gap-3">
                <div className="font-medium">{issue.user.display_name}</div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onViewUser(issue.user.user_uuid)}
                >
                    View
                </Button>
            </div>
        </div>
    );
}

export default function MerchantChecklistPage() {
    const navigate = useNavigate();
    const params = useParams();
    const organizationUuid: string = parseMerchantChecklistOrganizationUuid(params.id);
    const activeRoleAssignmentId: number | null = useAuthStore((state) => state.activeRoleAssignmentId);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
    const [selectedCredential, setSelectedCredential] = useState<SecretActionButtonsCredential | null>(
        null
    );
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [credentialToDelete, setCredentialToDelete] = useState<SecretActionButtonsCredential | null>(
        null
    );
    const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
    const [credentialToDisconnect, setCredentialToDisconnect] =
        useState<SecretActionButtonsCredential | null>(null);
    const [selectedUsageKeys, setSelectedUsageKeys] = useState<string[]>([]);

    const deleteMutation = useDeleteSecret();
    const disableMutation = useDisableSecret();
    const enableMutation = useEnableSecret();
    const disconnectMutation = useDisconnectCredentialUsages();
    const organizationReference: SecretOrganizationUuidReference = {
        organization_uuid: organizationUuid,
    };
    const credentialIdentityForDisconnectDialog: SecretUuidReference | undefined =
        disconnectDialogOpen && credentialToDisconnect !== null
            ? toChecklistSecretIdentityInput(credentialToDisconnect)
            : undefined;
    const {
        data: credentialUsagesResponse,
        isLoading: isCredentialUsagesLoading,
        error: credentialUsagesError,
    } = useCredentialUsages(credentialIdentityForDisconnectDialog);

    const checklistQueries = useQueries({
        queries: CHECKLIST_CHECK_CODES.map((checkCode) => ({
            queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'organization',
                organizationUuid,
                'merchant-checklist',
                checkCode,
            ]),
            queryFn: async (): Promise<GetMerchantChecklistCheckResponse> =>
                await organizationApi.getMerchantChecklistCheck(organizationUuid, checkCode),
            staleTime: 300000,
            gcTime: 1800000,
        })),
    });
    const checklistChecks: MerchantChecklistCheckResult[] = checklistQueries.flatMap((queryResult) =>
        queryResult.data !== undefined ? [queryResult.data.check] : []
    );
    const checklistErrorCount: number = checklistChecks.filter((check) => check.status === 'error').length;
    const checklistWarningCount: number = checklistChecks.filter((check) => check.status === 'warning').length;
    const checklistSummaryStatus: 'pass' | 'error' | 'warning' =
        checklistErrorCount > 0 ? 'error' : checklistWarningCount > 0 ? 'warning' : 'pass';
    const hasChecklistPendingQuery: boolean = checklistQueries.some((queryResult) => queryResult.isPending);

    const handleViewCredential = (credential: SecretActionButtonsCredential): void => {
        setSelectedCredential(credential);
        setModalMode('view');
        setModalOpen(true);
    };
    const handleViewUser = (userUuid: string): void => {
        Promise.resolve(navigate(buildChecklistUserHref(userUuid))).catch(
            (navigationError: unknown) => {
                console.error('Failed to navigate to user detail page', navigationError);
            }
        );
    };
    const handleOpenMerchantBusinessDetails = (): void => {
        runInBackground(
            navigate(buildMerchantBusinessDetailsHref(organizationUuid)),
            (_error: unknown) => undefined
        );
    };
    const handleOpenPermissionsAudit = (): void => {
        runInBackground(
            navigate(buildMerchantChecklistOrganizationAuditHref(organizationUuid)),
            (_error: unknown) => undefined
        );
    };
    const handleReviewPermissionIssue = (
        issue: MerchantChecklistPermissionIssue
    ): void => {
        Promise.resolve(
            navigate(
                buildMerchantChecklistPermissionAuditHref(
                    organizationUuid,
                    issue.role_assignment_uuid
                )
            )
        ).catch((navigationError: unknown) => {
            console.error('Failed to navigate to permissions audit page', navigationError);
        });
    };
    const handleEditCredential = (credential: SecretActionButtonsCredential): void => {
        setSelectedCredential(credential);
        setModalMode('edit');
        setModalOpen(true);
    };
    const handleDisableCredential = (credential: SecretActionButtonsCredential): void => {
        disableMutation.mutate({
            secret: toChecklistSecretIdentityInput(credential),
            organization: organizationReference,
        });
    };
    const handleEnableCredential = (credential: SecretActionButtonsCredential): void => {
        enableMutation.mutate({
            secret: toChecklistSecretIdentityInput(credential),
            organization: organizationReference,
        });
    };
    const handleDeleteCredential = (credential: SecretActionButtonsCredential): void => {
        setCredentialToDelete(credential);
        setDeleteConfirmOpen(true);
    };
    const handleConfirmDeleteCredential = (): void => {
        if (credentialToDelete === null) {
            return;
        }
        deleteMutation.mutate(
            {
                secret: toChecklistSecretIdentityInput(credentialToDelete),
                organization: organizationReference,
            },
            {
                onSuccess: () => {
                    setDeleteConfirmOpen(false);
                    setCredentialToDelete(null);
                },
            }
        );
    };
    const handleDisconnectCredential = (credential: SecretActionButtonsCredential): void => {
        setCredentialToDisconnect(credential);
        setSelectedUsageKeys([]);
        setDisconnectDialogOpen(true);
    };
    const handleToggleUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ): void => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedUsageKeys((previousSelectionKeys) =>
                previousSelectionKeys.includes(usageSelectionKey)
                    ? previousSelectionKeys
                    : [...previousSelectionKeys, usageSelectionKey]
            );
            return;
        }
        setSelectedUsageKeys((previousSelectionKeys) =>
            previousSelectionKeys.filter(
                (previousSelectionKey) => previousSelectionKey !== usageSelectionKey
            )
        );
    };
    const handleConfirmDisconnect = (): void => {
        if (credentialToDisconnect === null) {
            return;
        }
        const availableUsages: CredentialUsageListItem[] = credentialUsagesResponse?.usages ?? [];
        const selectedUsages: CredentialUsageReference[] = availableUsages
            .filter((usage) => selectedUsageKeys.includes(buildCredentialUsageSelectionKey(usage)))
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        disconnectMutation.mutate(
            {
                secret: toChecklistSecretIdentityInput(credentialToDisconnect),
                organization: organizationReference,
                usages: selectedUsages,
            },
            {
                onSuccess: () => {
                    setDisconnectDialogOpen(false);
                    setCredentialToDisconnect(null);
                    setSelectedUsageKeys([]);
                },
            }
        );
    };

    return (
        <div className="space-y-6">
            <BackButton
                href={buildMerchantChecklistBackHref(organizationUuid)}
                tooltip="Back to Organization Details"
            />
            <div className="space-y-2">
                <h1 className="text-3xl font-bold">Merchant Configuration Checklist</h1>
                {!hasChecklistPendingQuery ? (
                    <ChecklistSummaryBadge
                        status={checklistSummaryStatus}
                        errorCount={checklistErrorCount}
                        warningCount={checklistWarningCount}
                    />
                ) : null}
            </div>
            {hasChecklistPendingQuery ? (
                <div className="text-sm text-muted-foreground">Running checks...</div>
            ) : null}
            {deleteMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {deleteMutation.error instanceof Error
                            ? deleteMutation.error.message
                            : 'Failed to delete credential'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {disableMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {disableMutation.error instanceof Error
                            ? disableMutation.error.message
                            : 'Failed to disable credential'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {enableMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {enableMutation.error instanceof Error
                            ? enableMutation.error.message
                            : 'Failed to enable credential'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {disconnectMutation.error !== null ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {disconnectMutation.error instanceof Error
                            ? disconnectMutation.error.message
                            : 'Failed to disconnect credential usages'}
                    </AlertDescription>
                </Alert>
            ) : null}
            {CHECKLIST_CHECK_CODES.map((checkCode, checkIndex) => {
                const checklistQuery = checklistQueries[checkIndex];
                if (checklistQuery === undefined) {
                    return null;
                }
                if (checklistQuery.isPending) {
                    return (
                        <Card key={checkCode}>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0">
                                <CardTitle className="text-base">{CHECKLIST_LOADING_TITLES[checkCode]}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Running check...
                                </div>
                            </CardContent>
                        </Card>
                    );
                }
                if (checklistQuery.error !== null) {
                    return (
                        <ChecklistQueryErrorCard
                            key={checkCode}
                            checkCode={checkCode}
                            errorMessage={
                                checklistQuery.error instanceof Error
                                    ? checklistQuery.error.message
                                    : 'Failed to run this check'
                            }
                            onOpenMerchantBusinessDetails={
                                handleOpenMerchantBusinessDetails
                            }
                            onOpenPermissionsAudit={handleOpenPermissionsAudit}
                        />
                    );
                }
                if (checklistQuery.data === undefined) {
                    return null;
                }
                const check: MerchantChecklistCheckResult = checklistQuery.data.check;
                const isTerminalDeviceCredentialLinkIntegrityCheck: boolean =
                    check.code === 'terminal_device_credential_link_integrity';
                const aggregatedTerminalQaIssues: AggregatedTerminalQaIssue[] =
                    isTerminalDeviceCredentialLinkIntegrityCheck
                        ? aggregateTerminalQaIssues(check.issues)
                        : [];
                const nonTerminalIssues: MerchantChecklistIssue[] =
                    isTerminalDeviceCredentialLinkIntegrityCheck
                        ? check.issues.filter((issue) => issue.issue_type !== 'terminal')
                        : check.issues;
                return (
                    <Card key={check.code}>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0">
                            <CardTitle className="text-base">{check.title}</CardTitle>
                            <ChecklistCheckStatusBadge check={check} />
                        </CardHeader>
                        {check.status !== 'pass' ? (
                            <CardContent>
                                {check.details.length > 0 && check.issues.length === 0 ? (
                                    <div className="space-y-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="text-sm">{check.details[0]}</div>
                                            <ChecklistTopLevelAction
                                                check={check}
                                                onOpenMerchantBusinessDetails={
                                                    handleOpenMerchantBusinessDetails
                                                }
                                                onOpenPermissionsAudit={
                                                    handleOpenPermissionsAudit
                                                }
                                            />
                                        </div>
                                        {check.details.length > 1 ? (
                                            <ul className="list-disc pl-5 space-y-1">
                                                {check.details.slice(1).map((detail) => (
                                                    <li key={detail} className="text-sm">
                                                        {detail}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                    </div>
                                ) : (
                                    <>
                                {check.issues.length > 0 ? (
                                <div className="space-y-3">
                                    <div className="flex justify-end">
                                        <ChecklistTopLevelAction
                                            check={check}
                                            onOpenMerchantBusinessDetails={
                                                handleOpenMerchantBusinessDetails
                                            }
                                            onOpenPermissionsAudit={handleOpenPermissionsAudit}
                                        />
                                    </div>
                                    {aggregatedTerminalQaIssues.map((aggregatedIssue) => (
                                        <TerminalDeviceQaCard
                                            key={getTerminalIssueGroupKey({
                                                issue_type: 'terminal',
                                                message: '',
                                                terminal: aggregatedIssue.terminal,
                                            })}
                                            terminal={aggregatedIssue.terminal}
                                            failedChecks={aggregatedIssue.failedChecks}
                                        />
                                    ))}
                                    {nonTerminalIssues.map((issue, issueIndex) => (
                                        <ChecklistIssueCard
                                            key={`${check.code}-${issue.issue_type}-${issueIndex}`}
                                            issue={issue}
                                            organizationUuid={organizationUuid}
                                            onViewUser={handleViewUser}
                                            onReviewPermissionIssue={
                                                handleReviewPermissionIssue
                                            }
                                            onViewCredential={handleViewCredential}
                                            onEditCredential={handleEditCredential}
                                            onDisableCredential={handleDisableCredential}
                                            onEnableCredential={handleEnableCredential}
                                            onDeleteCredential={handleDeleteCredential}
                                            onDisconnectCredential={handleDisconnectCredential}
                                        />
                                    ))}
                                </div>
                                ) : check.details.length > 0 ? (
                                <ul className="list-disc pl-5 space-y-1">
                                    {check.details.map((detail) => (
                                        <li key={detail} className="text-sm">
                                            {detail}
                                        </li>
                                    ))}
                                </ul>
                                ) : (
                                <div className="text-sm text-muted-foreground">
                                    Check failed; no renderable issue records were returned.
                                </div>
                                )}
                                    </>
                                )}
                            </CardContent>
                        ) : null}
                    </Card>
                );
            })}

            <SecretModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                organizationUuid={organizationUuid}
                organizationName="Merchant organization"
                secret={selectedCredential ?? undefined}
                mode={modalMode}
            />

            <ConfirmationModal
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
                message={
                    credentialToDelete !== null
                        ? `Are you sure you want to delete credential "${credentialToDelete.account_id}"?`
                        : ''
                }
                onConfirm={handleConfirmDeleteCredential}
                confirmLabel="Delete"
                isLoading={deleteMutation.isPending}
            />

            <Dialog
                open={disconnectDialogOpen}
                onOpenChange={(isOpen) => {
                    setDisconnectDialogOpen(isOpen);
                    if (!isOpen) {
                        setCredentialToDisconnect(null);
                        setSelectedUsageKeys([]);
                    }
                }}
            >
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>Disconnect Credential</DialogTitle>
                        <DialogDescription>
                            Select one or more usages to disconnect from this credential.
                        </DialogDescription>
                    </DialogHeader>

                    {isCredentialUsagesLoading ? (
                        <p className="text-sm text-muted-foreground">Loading credential usages...</p>
                    ) : credentialUsagesError ? (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {credentialUsagesError instanceof Error
                                    ? credentialUsagesError.message
                                    : 'Failed to load credential usages'}
                            </AlertDescription>
                        </Alert>
                    ) : (credentialUsagesResponse?.usages.length ?? 0) === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            This credential is not currently linked to any usage.
                        </p>
                    ) : (
                        <div className="space-y-3 max-h-72 overflow-auto pr-2">
                            {(credentialUsagesResponse?.usages ?? []).map((usage) => {
                                const usageSelectionKey: string =
                                    buildCredentialUsageSelectionKey(usage);
                                const isSelected: boolean = selectedUsageKeys.includes(
                                    usageSelectionKey
                                );
                                return (
                                    <label
                                        key={usageSelectionKey}
                                        className="flex items-start gap-3 rounded border p-3 cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked: boolean) =>
                                                handleToggleUsageSelection(usage, checked)
                                            }
                                        />
                                        <span className="text-sm">{usage.display_name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDisconnectDialogOpen(false);
                                setCredentialToDisconnect(null);
                                setSelectedUsageKeys([]);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDisconnect}
                            disabled={selectedUsageKeys.length === 0 || disconnectMutation.isPending}
                        >
                            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
